import {
  AccessLevels,
  invalidateAccessControlQueries,
  toast,
  useAssignUsers,
  useUpdateAssignUsers,
} from '@lovoldsystem/authorization.client'
import {
  IClientUserWithAccess,
  IUserBasicDetails,
} from '@lovoldsystem/authorization.client/components/users/authorizationMapping.action'
import { Box, Chip, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AppForm, FormTextField } from 'src/components/FormCore'
import { CancelButton } from 'src/components/CancelButton'
import { MainButton } from 'src/components/MainButton'
import { FunctionElementsParentsIds } from 'src/enums/auth'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { z } from 'zod'

interface LovoldAsAssignUsersDialogProps extends ShowDialogProps {
  clientId: number
  assignCompleted: (users: any) => void
  // Update-only: identifies the user/link being edited. Absent when adding a new user.
  clientUserLinks?: IClientUserWithAccess[]
  user?: IUserBasicDetails
}

// Auth_FunctionalElements row 22 (Core.Consts.FunctionalElementConstants.ProjectPlanning). The bulk-assign
// endpoints derive each permission's value from functionalElement.name + permissionLevel and drop any item
// without a functionalElement, so the name must be sent too.
const PROJECT_PLANNING_ELEMENT = {
  id: 22,
  name: 'ProjectPlanning',
  parentId: FunctionElementsParentsIds.ProjectPlanningPortal,
}

// ProjectPlanning Write as a direct permission - on a Lovold AS client link, that one permission is what makes
// the user a System Project Admin. Their headers get the other ProjectPlanning bits from the backend.
const SYSTEM_PROJECT_ADMIN_PERMISSIONS = [
  {
    functionalElementId: PROJECT_PLANNING_ELEMENT.id,
    functionalElement: PROJECT_PLANNING_ELEMENT,
    permissionLevel: AccessLevels.Write,
    permissionValue: 0,
  },
]

/**
 * Lovold AS has no localities/service stations/storages, and for now the only access it should ever
 * grant is System Project Admin - ProjectPlanning Write, which gives blanket access to every project it
 * created, nothing to scope down to. So unlike the generic customer AssignUsers/UpdateUsers
 * dialog, there's no role picker and no facility picker here - just an email (mirroring the old,
 * now-deleted ProjectAdminDialog's single email field), submitted through the same bulk-assign endpoints
 * the generic dialog uses. No "Valid until" either: System Project Admin access doesn't expire, and the
 * SPA check never reads it.
 */
const LovoldAsAssignUsersDialog: React.FC<LovoldAsAssignUsersDialogProps> = ({
  pop,
  clientId,
  assignCompleted,
  clientUserLinks,
  user,
}) => {
  const { t: translate } = useTranslation(['users', 'projects', 'access-control', 'common'])
  const theme = useTheme()
  const { mutateAsync: assignUsers, isLoading: isAssigning } = useAssignUsers()
  const { mutateAsync: updateUsers, isLoading: isUpdating } = useUpdateAssignUsers()

  const existingLink = clientUserLinks?.[0]
  const isUpdate = user != null

  const userSchema = z.object({
    email: z.string().email(),
  })
  const form = useZodForm(userSchema, { defaultValues: { email: user?.email ?? '' } })

  const onSubmit = async ({ email }: { email: string }) => {
    const payload = {
      users: isUpdate ? [{ id: user!.id, email }] : [{ id: null, email }],
      permissions: SYSTEM_PROJECT_ADMIN_PERMISSIONS,
      roleId: null,
      clientId,
      facilityIds: [],
      validUntil: null,
    }

    if (isUpdate && existingLink) {
      await updateUsers(
        { ...payload, Id: existingLink.clientUserLinkId },
        {
          onSuccess: () => {
            toast.success(translate('successfullyUpdated', { ns: 'common-api-message' }))
            invalidateAccessControlQueries.invalidateGetDirectPermissions(clientId, user!.id)
            assignCompleted([user])
            pop()
          },
        },
      )
    } else {
      await assignUsers(payload, {
        onSuccess: () => {
          toast.success(translate('successfullyAdded', { ns: 'common-api-message' }))
          assignCompleted(payload.users)
          pop()
        },
      })
    }
  }

  return (
    <AppForm form={form} onSubmit={onSubmit}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormTextField name="email" placeholder={translate('email', { ns: 'users' })} disabled={isUpdate} />
        <Chip
          label={translate('full-access-to-client', { ns: 'access-control' })}
          sx={{
            alignSelf: 'flex-start',
            background: `${getThemeColor(theme, MUIThemeColors.primaryLight)}44`,
            color: getThemeColor(theme, MUIThemeColors.primaryDark),
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <CancelButton onClick={() => pop()} />
          <MainButton key="add-user-button" type="submit" variant="contained" loading={isAssigning || isUpdating}>
            {isUpdate ? translate('update', { ns: 'common' }) : translate('add-user', { ns: 'projects' })}
          </MainButton>
        </Box>
      </Box>
    </AppForm>
  )
}

export default LovoldAsAssignUsersDialog
